import { useState } from "react";
import { Editor } from "tldraw";

import {
  BindableShape,
  BindableUtil,
} from "@/components/canvas-editor/shapes/bindable";
import { ListItem, UnorderedList } from "@/components/ui/typography";

export function NodeDocsHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-lg font-semibold mb-2 flex items-center">{children}</h2>
  );
}

export function NodeDocsSubHeader({ children }: { children: React.ReactNode }) {
  return <h2 className="font-semibold mb-2">{children}</h2>;
}

export function NodeDocsDescription({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="text-sm mb-4">{children}</div>;
}

type ExtractShape<T> = T extends BindableUtil<infer U> ? U : never;

export function useExampleShape<U extends BindableUtil<BindableShape>>(
  util: U,
  defaultProps?: Partial<ExtractShape<U>["props"]>,
  extraProps?: Record<string, unknown>,
) {
  const [props, setProps] = useState({
    ...util.getDefaultProps(),
    ...defaultProps,
    ...extraProps,
  });
  const updateShape = (_: unknown, update: Partial<object>) => {
    setProps({ ...props, ...update } as typeof props);
  };

  return {
    shape: { props: { ...props, ...extraProps } },
    updateShape,
    className: "relative!",
    "data-example": true,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Constructor<T, Arguments extends unknown[] = any[]> = {
  new (...arguments_: Arguments): T;
  docs: {
    title: string;
    description?: React.ReactNode;
  };
};

export function NodeDocs<U extends BindableUtil<BindableShape>>({
  editor,
  util: Util,
  component: Component,
  extraProps,
  title,
  description,
}: {
  editor: Editor;
  util: Constructor<U>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  extraProps?: Record<string, unknown>;
  title?: string;
  description?: React.ReactNode;
}) {
  const example = useExampleShape(new Util(editor), undefined, extraProps);

  return (
    <div
      style={{
        width: Math.max(400, example.shape.props.minW || example.shape.props.w),
      }}
    >
      <NodeDocsHeader>{title || Util.docs.title}</NodeDocsHeader>
      <NodeDocsDescription>
        {description || Util.docs.description}
      </NodeDocsDescription>
      <div className="w-full overflow-auto">
        <Component {...example} />
      </div>
    </div>
  );
}

export const modelProbabilityExplanation = (
  <div>
    <div>The probability that the post matches the model.</div>
    <UnorderedList className="my-2">
      <ListItem className="my-[4px]">{"0.3 => Somewhat likely"}</ListItem>
      <ListItem className="my-[4px]">{"0.7 => Pretty likely"}</ListItem>
      <ListItem className="my-[4px]">{"0.9 => Very likely"}</ListItem>
    </UnorderedList>
  </div>
);
